import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Trash as TrashIcon, Plus, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fc = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const REASONS = [
  { value: "vencido", label: "Vencido" },
  { value: "quebra", label: "Quebra/Dano" },
  { value: "sobra", label: "Sobra de Produção" },
  { value: "preparo", label: "Perda no Preparo" },
  { value: "outro", label: "Outro" },
];

const REASON_COLORS = {
  vencido: "bg-rose-100 text-rose-700",
  quebra: "bg-orange-100 text-orange-700",
  sobra: "bg-amber-100 text-amber-700",
  preparo: "bg-blue-100 text-blue-700",
  outro: "bg-zinc-100 text-zinc-600",
};

export default function WasteControl() {
  const [records, setRecords] = useState([]);
  const { t } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ingredient_id: "", ingredient_name: "", quantity: "", unit: "kg", reason: "sobra", estimated_cost: "", notes: "", date: new Date().toISOString().slice(0, 10) });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [recRes, sumRes, ingRes] = await Promise.all([
        axios.get(`${API}/waste`),
        axios.get(`${API}/waste/summary`),
        axios.get(`${API}/ingredients`),
      ]);
      setRecords(recRes.data);
      setSummary(sumRes.data);
      setIngredients(ingRes.data);
    } catch { toast.error("Erro ao carregar dados"); }
    finally { setLoading(false); }
  };

  const handleIngredientSelect = (ingId) => {
    const ing = ingredients.find(i => i.id === ingId);
    if (ing) setForm({ ...form, ingredient_id: ingId, ingredient_name: ing.name, unit: ing.unit });
  };

  const handleSave = async () => {
    if (!form.ingredient_name.trim() || !form.quantity) { toast.error("Preencha ingrediente e quantidade"); return; }
    try {
      await axios.post(`${API}/waste`, { ...form, quantity: parseFloat(form.quantity), estimated_cost: parseFloat(form.estimated_cost) || 0 });
      toast.success("Registro de desperdício salvo!");
      setDialogOpen(false);
      setForm({ ingredient_id: "", ingredient_name: "", quantity: "", unit: "kg", reason: "sobra", estimated_cost: "", notes: "", date: new Date().toISOString().slice(0, 10) });
      fetchData();
    } catch { toast.error("Erro ao salvar"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deletar este registro?")) return;
    try { await axios.delete(`${API}/waste/${id}`); toast.success("Deletado!"); fetchData(); }
    catch { toast.error("Erro"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" /></div>;

  return (
    <div className="space-y-6" data-testid="waste-control-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("ws_title")}</h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">{t("ws_subtitle")}</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="add-waste-btn">
          <Plus size={16} className="mr-2" /> {t('ws_new')}
        </Button>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-zinc-200 shadow-sm">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Registros (Mês)</p>
              <p className="text-lg md:text-2xl font-mono font-bold text-zinc-900 mt-1">{summary.total_records}</p>
            </CardContent>
          </Card>
          <Card className="border-rose-200 bg-rose-50 shadow-sm">
            <CardContent className="pt-4 pb-3 px-4">
              <p className="text-[10px] uppercase tracking-wider text-rose-600 font-bold">Custo Total Perdas</p>
              <p className="text-lg md:text-2xl font-mono font-bold text-rose-700 mt-1" data-testid="waste-total-cost">{fc(summary.total_cost)}</p>
            </CardContent>
          </Card>
          {Object.entries(summary.by_reason || {}).slice(0, 2).map(([reason, cost]) => (
            <Card key={reason} className="border-zinc-200 shadow-sm">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{REASONS.find(r => r.value === reason)?.label || reason}</p>
                <p className="text-lg md:text-2xl font-mono font-bold text-zinc-900 mt-1">{fc(cost)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {records.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <Warning size={48} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500">{t("ws_no_records")}</p>
        </CardContent></Card>
      ) : (
        <div className="border border-zinc-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-zinc-50">
              <TableHead className="text-xs font-semibold">Data</TableHead>
              <TableHead className="text-xs font-semibold">Ingrediente</TableHead>
              <TableHead className="text-xs font-semibold text-right">Quantidade</TableHead>
              <TableHead className="text-xs font-semibold text-center">Motivo</TableHead>
              <TableHead className="text-xs font-semibold text-right">Custo Est.</TableHead>
              <TableHead className="text-xs font-semibold text-center">Ação</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {records.slice(0, 50).map(r => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs text-zinc-500">{r.date}</TableCell>
                  <TableCell className="text-sm font-medium">{r.ingredient_name}</TableCell>
                  <TableCell className="text-sm text-right font-mono">{r.quantity} {r.unit}</TableCell>
                  <TableCell className="text-center">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${REASON_COLORS[r.reason] || REASON_COLORS.outro}`}>
                      {REASONS.find(rr => rr.value === r.reason)?.label || r.reason}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-right font-mono text-rose-600">{fc(r.estimated_cost)}</TableCell>
                  <TableCell className="text-center">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => handleDelete(r.id)}>
                      <TrashIcon size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>{t("ws_new")}</DialogTitle>
            <DialogDescription className="sr-only">Registrar perda de ingrediente</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Ingrediente</Label>
              <Select value={form.ingredient_id} onValueChange={handleIngredientSelect}>
                <SelectTrigger className="mt-1.5" data-testid="waste-ingredient-select"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {ingredients.map(ing => <SelectItem key={ing.id} value={ing.id}>{ing.name} ({ing.unit})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Quantidade</Label>
                <Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({...form, quantity: e.target.value})} className="mt-1.5" data-testid="waste-quantity" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Unidade</Label>
                <Input value={form.unit} disabled className="mt-1.5 bg-zinc-50" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Custo Est.</Label>
                <Input type="number" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({...form, estimated_cost: e.target.value})} placeholder="R$" className="mt-1.5" data-testid="waste-cost" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Motivo</Label>
                <Select value={form.reason} onValueChange={(v) => setForm({...form, reason: v})}>
                  <SelectTrigger className="mt-1.5" data-testid="waste-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Data</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="mt-1.5" />
              </div>
            </div>
            <Button onClick={handleSave} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="save-waste-btn">
              {t('ws_new')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
